<script lang="ts">
  import { _, locale } from 'svelte-i18n';
  import type { UploadedDocument } from '../api';
  import { uploadSessionStore } from '../stores/uploadSessionStore';
  import { categoriesStore } from '../stores/categoriesStore';
  import { getLocalizedCombinedCategoryLabel } from '../categoriesLocalization';
  import { FileText } from 'lucide-svelte';
  import { StatusBadge, TableSortHeader } from './ui/index';

  export let documents: UploadedDocument[] = [];
  export let selectedDocumentId: string | null = null;

  // Client-side sort state
  let clientSortKey: string | null = null;
  let clientSortOrder: 'asc' | 'desc' | null = null;

  const statusOrder: Record<string, number> = {
    uploaded: 0,
    pending: 1,
    classifying: 2,
    classified: 3,
    'ocr-processing': 4,
    'ocr-completed': 5,
    extracting: 6,
    processed: 7,
    completed: 8,
    finished: 9,
    failed: 10,
    error: 11,
  };

  $: sortedDocuments = sortDocuments(documents, clientSortKey, clientSortOrder);

  function sortDocuments(
    docs: UploadedDocument[],
    key: string | null,
    order: string | null,
  ): UploadedDocument[] {
    if (!key || !order) return docs;
    const sorted = [...docs];
    const dir = order === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      if (key === 'fileName') {
        return dir * a.fileName.localeCompare(b.fileName);
      } else if (key === 'status') {
        return dir * ((statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99));
      } else if (key === 'fileSize') {
        return dir * (a.fileSize - b.fileSize);
      }
      return 0;
    });
    return sorted;
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    clientSortKey = e.detail.order ? e.detail.key : null;
    clientSortOrder = e.detail.order;
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  function handleRowClick(doc: UploadedDocument) {
    uploadSessionStore.selectDocument(doc.id);
  }
</script>

{#if documents.length === 0}
  <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
    <FileText class="w-8 h-8 text-text-muted mb-2" strokeWidth={1.5} />
    <p class="text-sm text-text-secondary">{$_('upload.table.no_documents')}</p>
  </div>
{:else}
  <table class="w-full">
    <thead>
      <tr class="border-b border-border">
        <TableSortHeader
          label={$_('upload.table.status')}
          sortKey="status"
          currentSortBy={clientSortKey}
          currentSortOrder={clientSortOrder}
          thClass="w-12"
          on:sort={handleSort}
        />
        <TableSortHeader
          label={$_('upload.table.filename')}
          sortKey="fileName"
          currentSortBy={clientSortKey}
          currentSortOrder={clientSortOrder}
          on:sort={handleSort}
        />
        <th
          class="w-48 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden sm:table-cell"
        >
          {$_('upload.table.classification')}
        </th>
        <TableSortHeader
          label={$_('upload.table.size')}
          sortKey="fileSize"
          currentSortBy={clientSortKey}
          currentSortOrder={clientSortOrder}
          thClass="w-20 hidden sm:table-cell"
          on:sort={handleSort}
        />
      </tr>
    </thead>
    <tbody>
      {#each sortedDocuments as doc, idx (doc.id)}
        <tr
          data-testid="upload-row-{idx}"
          on:click={() => handleRowClick(doc)}
          class="border-b border-border/50 cursor-pointer transition-colors hover:bg-surface-hover
            {doc.id === selectedDocumentId ? 'bg-accent/10 border-l-2 border-l-accent' : ''}"
          role="button"
          tabindex="0"
          on:keydown={(e) => e.key === 'Enter' && handleRowClick(doc)}
        >
          <td class="px-3 py-3 text-center" data-testid="upload-status-{idx}">
            <StatusBadge status={doc.status} />
          </td>
          <td class="px-3 py-3">
            <a
              href="/documents/{doc.id}"
              class="text-sm font-medium text-accent hover:text-accent-hover truncate block"
              on:click|preventDefault
            >
              {doc.fileName}
            </a>
          </td>
          <td class="px-3 py-3 hidden sm:table-cell">
            {#if doc.classification}
              <span class="text-sm text-text">
                {getLocalizedCombinedCategoryLabel(
                  doc.classification.category,
                  doc.classification.subCategory ?? null,
                  $locale,
                )}
              </span>
            {:else}
              <span class="text-sm text-text-muted">—</span>
            {/if}
          </td>
          <td class="px-3 py-3 text-right text-sm text-text-muted hidden sm:table-cell">
            {doc.fileSize > 0 ? formatFileSize(doc.fileSize) : '—'}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
{/if}
